import * as io from "io-ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDelete, useGet, usePost } from "../useCall";
import { useCallback, useMemo } from "react";
import { useChannel } from "../ws/useChannel";
import type { Input } from "../call";
import { useEvents, type TypedEvent } from "../useEvents";
import { generateFaultEventPayload } from "diagnostics-domain/faultEvent";

const FaultEventDashboardRouteParams = io.strict({
  projectId: io.string,
  faultEventId: io.string,
});

const ManualFaultEventDashboardRouteParams = io.strict({
  projectId: io.string,
  manualFaultEventId: io.string,
});

export const useEventsDashboards = (routeParams: { projectId: string }) => {
  const queryClient = useQueryClient();
  const listManualFaultEventsDashboards = useGet(
    "/projects/:projectId/manual-fault-events/dashboards",
  );
  const listFaultEventsDashboards = useGet("/projects/:projectId(uuid)/fault-events/dashboards");
  const manualFaultEventsDashboards = useQuery({
    queryKey: ["manual-fault-event-dashboards", Object.entries(routeParams).flat()],
    queryFn: () => listManualFaultEventsDashboards({ routeParams }),
  });
  const faultEventsDashboards = useQuery({
    queryKey: ["fault-event-dashboards", Object.entries(routeParams).flat()],
    queryFn: () => listFaultEventsDashboards({ routeParams }),
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ["manual-fault-event-dashboards", ...Object.entries(routeParams).flat()],
    });
    void queryClient.invalidateQueries({
      queryKey: ["fault-event-dashboards", ...Object.entries(routeParams).flat()],
    });
  }, [queryClient, routeParams]);

  useChannel(
    { key: "manual_fault_event_dashboard", projectId: routeParams.projectId },
    invalidate,
    true,
  );
  useChannel(
    { key: "fault_events_dashboards", projectId: routeParams.projectId },
    invalidate,
    true,
  );

  return useMemo(
    () => [
      ...(manualFaultEventsDashboards.data?.body ?? []),
      ...(faultEventsDashboards.data?.body ?? []),
    ],
    [manualFaultEventsDashboards, faultEventsDashboards],
  );
};

export const useEventDashboards = (
  routeParams:
    | Input<
        "GET",
        "/projects/:projectId(uuid)/faultEvents/:faultEventId(uuid)/dashboards"
      >["routeParams"]
    | Input<
        "GET",
        "/projects/:projectId/manual-fault-events/:manualFaultEventId/dashboards"
      >["routeParams"],
) => {
  const queryClient = useQueryClient();
  const listFaultEventDashboards = useGet(
    "/projects/:projectId(uuid)/faultEvents/:faultEventId(uuid)/dashboards",
  );
  const listManualFaultEventDashboards = useGet(
    "/projects/:projectId/manual-fault-events/:manualFaultEventId/dashboards",
  );

  const isFaultEventDashboardParams = FaultEventDashboardRouteParams.is(routeParams);
  const isManualFaultEventDashboardParams = ManualFaultEventDashboardRouteParams.is(routeParams);
  const key = isFaultEventDashboardParams
    ? "fault-event"
    : isManualFaultEventDashboardParams
      ? "manual-fault-event"
      : null;

  const faultEventDashboardResult = useQuery({
    enabled: isFaultEventDashboardParams,
    queryKey: ["fault-event", "dashboards", ...Object.entries(routeParams).flat()],
    queryFn: () => {
      if (isFaultEventDashboardParams) {
        return listFaultEventDashboards({ routeParams });
      }
      return;
    },
    suspense: true,
  });

  const manualFaultEventDashboardResult = useQuery({
    enabled: isManualFaultEventDashboardParams,
    queryKey: ["manual-fault-event", "dashboards", ...Object.entries(routeParams).flat()],
    queryFn: () => {
      if (isManualFaultEventDashboardParams) {
        return listManualFaultEventDashboards({ routeParams });
      }
      return;
    },
    suspense: true,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [key, "dashboards", ...Object.entries(routeParams).flat()],
    });
  }, [key, queryClient, routeParams]);

  useChannel(
    { key: "manual_fault_event_dashboard", projectId: routeParams.projectId },
    invalidate,
    true,
  );
  useChannel(
    { key: "fault_events_dashboards", projectId: routeParams.projectId },
    invalidate,
    true,
  );

  return useMemo(
    () => [
      ...(faultEventDashboardResult.data?.body ?? []),
      ...(manualFaultEventDashboardResult.data?.body ?? []),
    ],
    [faultEventDashboardResult, manualFaultEventDashboardResult],
  );
};

export const useLinkEventDashboards = (
  routeParams:
    | Input<
        "POST",
        "/projects/:projectId(uuid)/faultEvents/:faultEventId(uuid)/dashboards"
      >["routeParams"]
    | Input<
        "POST",
        "/projects/:projectId/manual-fault-events/:manualFaultEventId/dashboards"
      >["routeParams"],
) => {
  const events = useEvents(routeParams.projectId, false);

  const faultEventToCreate = useMemo(
    () =>
      events.find(
        (event): event is TypedEvent<"DIAGNOSED"> =>
          FaultEventDashboardRouteParams.is(routeParams) &&
          event.type === "DIAGNOSED" &&
          event.id === routeParams.faultEventId &&
          event.createdAt === null,
      ),
    [events, routeParams],
  );

  const linkFaultEventDashboards = usePost(
    "/projects/:projectId(uuid)/faultEvents/:faultEventId(uuid)/dashboards",
  );

  const linkManualFaultEventDashboards = usePost(
    "/projects/:projectId/manual-fault-events/:manualFaultEventId/dashboards",
  );
  return useCallback(
    (dashboardIds: string[]) => {
      if (ManualFaultEventDashboardRouteParams.is(routeParams)) {
        return linkManualFaultEventDashboards({
          routeParams,
          body: {
            dashboardIds,
          },
        });
      }
      if (FaultEventDashboardRouteParams.is(routeParams)) {
        return linkFaultEventDashboards({
          routeParams,
          body: {
            dashboardIds,
            faultEventToCreate: faultEventToCreate
              ? generateFaultEventPayload({ faultEvent: faultEventToCreate })
              : undefined,
          },
        });
      }
      return Promise.reject(new Error("Invalid routeParams"));
    },
    [routeParams, linkFaultEventDashboards, linkManualFaultEventDashboards, faultEventToCreate],
  );
};

export const useUnlinkEventDashboards = (
  routeParams:
    | Input<
        "DELETE",
        "/projects/:projectId(uuid)/faultEvents/:faultEventId(uuid)/dashboards"
      >["routeParams"]
    | Input<
        "DELETE",
        "/projects/:projectId/manual-fault-events/:manualFaultEventId/dashboards"
      >["routeParams"],
) => {
  const unlinkFaultEventDashboards = useDelete(
    "/projects/:projectId(uuid)/faultEvents/:faultEventId(uuid)/dashboards",
  );
  const unlinkManualFaultEventDashboards = useDelete(
    "/projects/:projectId/manual-fault-events/:manualFaultEventId/dashboards",
  );
  return useCallback(
    (dashboardIds: string[]) => {
      if (ManualFaultEventDashboardRouteParams.is(routeParams)) {
        return unlinkManualFaultEventDashboards({
          routeParams,
          body: {
            dashboardIds,
          },
        });
      }
      if (FaultEventDashboardRouteParams.is(routeParams)) {
        return unlinkFaultEventDashboards({
          routeParams,
          body: {
            dashboardIds,
          },
        });
      }
      return Promise.reject(new Error("Invalid routeParams"));
    },
    [routeParams, unlinkFaultEventDashboards, unlinkManualFaultEventDashboards],
  );
};

export const useCreateAndLinkEventDashboard = (
  routeParams:
    | Input<
        "POST",
        "/projects/:projectId(uuid)/faultEvents/:faultEventId(uuid)/dashboards"
      >["routeParams"]
    | Input<
        "POST",
        "/projects/:projectId/manual-fault-events/:manualFaultEventId/dashboards"
      >["routeParams"],
) => {
  const createDashboard = usePost("/projects/:projectId(uuid)/dashboards");
  const linkEventDashboards = useLinkEventDashboards(routeParams);
  return useCallback(
    (title = "") =>
      createDashboard({
        routeParams: {
          projectId: routeParams.projectId,
        },
        body: {
          title,
          published: true,
          readonly: false,
          description: null,
          block: {
            texts: [],
            headings: [],
            comments: [],
            images: [],
            diagrams: [],
            plots: [],
            presets: [],
          },
        },
      }).then(result => linkEventDashboards([result.body.id]).then(() => result.body.id)),
    [routeParams.projectId, createDashboard, linkEventDashboards],
  );
};

export type EventDashboard = ReturnType<typeof useEventDashboards>[number];
